/**
 * BalanceScale — a 3-piece animated balance scale.
 *
 *   ┌─── balance-tray ─── balance-tray ───┐
 *   │         \              /            │
 *   │      balance-rod (horizontal)       │  ← pivots around the stand's hole
 *   │           \   pivot   /             │
 *   │         ▼ balance-stand ▼            │  ← fixed
 *
 * Assembled at runtime from `balance-stand`, `balance-rod`, `balance-tray`.
 *
 * The (x, y) constructor position is the pivot point — i.e. where the
 * hole in the top of the stand sits — so trays are easy to position
 * symmetrically left/right of x.
 *
 * Use `setBalance(ratio)` to tilt the scale:
 *   -1 → left pan fully down (left side heavy)
 *    0 → level
 *   +1 → right pan fully down (right side heavy)
 *
 * The trays act like containers — pass game objects to
 * `placeOnLeft(obj)` / `placeOnRight(obj)` and they'll sit on the pan
 * and tilt with it.
 *
 * `getLeftTrayPos()` / `getRightTrayPos()` return the world position of
 * each pan's top surface, useful for snapping incoming objects.
 */
export default class BalanceScale extends Phaser.GameObjects.Container {
  constructor(scene, x, y, options = {}) {
    super(scene, x, y);
    scene.add.existing(this);

    this.maxTiltDegrees = options.maxTiltDegrees ?? 16;
    this.tiltDuration = options.tiltDuration ?? 350;
    this.tiltEase = options.tiltEase ?? 'Sine.easeOut';

    // Asset-specific offsets. The hole in `balance-stand` sits ~22 px below
    // the image's top edge; `balance-rod`'s end circles sit ~12 px inside
    // each horizontal edge. These numbers tie the three sprites together
    // around a single (0, 0) pivot inside the container.
    const STAND_HOLE_Y = 22;
    const ROD_END_INSET = 12;
    const ROD_WIDTH = 509;
    const TRAY_HEIGHT = 175;
    // Within the tray image: the dish top is ~15 px below the image's top
    // edge, the stick bottom is at image bottom. Used to compute the pan
    // surface for placement.
    const TRAY_DISH_TOP_OFFSET = TRAY_HEIGHT - 15;

    this._rodHalfSpan = ROD_WIDTH / 2 - ROD_END_INSET;
    // Two slightly different y's:
    //  - trayTopY: where the tray-top rim ellipse is drawn
    //  - panSurfaceLocalY: where an object's bottom rests (a bit lower
    //    than the rim so the object reads as sitting deeper in the dish)
    this._trayTopLocalY = -TRAY_DISH_TOP_OFFSET + 40;
    // Each pan has its own resting y (more negative = higher on the
    // tray). Left holds the smaller target block; the right holds
    // weight bags which need to sit a bit higher.
    this._leftPanSurfaceLocalY = -85;
    this._rightPanSurfaceLocalY = -150;

    // Stand — fixed (does not rotate). Its hole aligns with (0, 0).
    this.stand = scene.add.image(0, -STAND_HOLE_Y, 'balance-stand').setOrigin(0.5, 0);

    // Pivot subcontainer — everything tilts together with this.
    this.pivot = scene.add.container(0, 0);

    // Trays as sub-containers so callers can `add` game objects to them.
    // Each tray-sprite has origin (0.5, 1) — its stick-bottom sits on the
    // rod end-circle, so the dish points UP. The tray-top sprite layers
    // the dish's rim ellipse above the tray bowl but behind any object
    // placed via `placeOnLeft/Right`, creating the illusion that objects
    // sit *inside* the dish.
    this.leftTray = scene.add.container(-this._rodHalfSpan, 0);
    this.rightTray = scene.add.container(this._rodHalfSpan, 0);

    // Clear depth bands inside each tray. Bags placed via attach*Pan
    // (depth 0, see GameScene) always sit between the rim (back) and
    // the bowl (front), so they read as "inside" the dish.
    this.leftTraySprite = scene.add
      .image(0, 0, 'balance-tray')
      .setOrigin(0.5, 1)
      .setDepth(10);
    this.rightTraySprite = scene.add
      .image(0, 0, 'balance-tray')
      .setOrigin(0.5, 1)
      .setDepth(10);

    // Tray-top oval — the BACK rim — sits at the dish-rim level.
    const trayTopY = this._trayTopLocalY;
    this.leftTrayTop = scene.add
      .image(0, trayTopY, 'balance-tray-top')
      .setOrigin(0.5, 0.5)
      .setDepth(-10);
    this.rightTrayTop = scene.add
      .image(0, trayTopY, 'balance-tray-top')
      .setOrigin(0.5, 0.5)
      .setDepth(-10);

    // Render order inside each tray (back → front): bowl (depth -2),
    // tray-top (depth -1), then any object the caller adds (default
    // depth 0). Phaser sorts container children by depth.
    this.leftTray.add([this.leftTraySprite, this.leftTrayTop]);
    this.rightTray.add([this.rightTraySprite, this.rightTrayTop]);

    // Rod, centered on pivot.
    this.rod = scene.add.image(0, 0, 'balance-rod').setOrigin(0.5, 0.5);

    // Render order (back → front): trays, rod (inside pivot), then stand
    // on top of everything so the upright neck covers the rod center.
    this.pivot.add([this.leftTray, this.rightTray, this.rod]);
    this.add([this.pivot, this.stand]);

    this._balance = 0;
  }

  /**
   * Tilt the scale.
   * @param {number} ratio  -1 (left heavy) to +1 (right heavy); 0 is level
   * @param {boolean} [animate=true]
   */
  setBalance(ratio, animate = true) {
    const clamped = Math.max(-1, Math.min(1, ratio));
    this._balance = clamped;

    // Left heavy → left pan down → rod rotates clockwise → positive angle
    // (Phaser's `angle` is in degrees, clockwise positive).
    const rodAngle = clamped * this.maxTiltDegrees;
    // Trays stay UPRIGHT regardless of rod tilt (real balance behavior).
    const trayAngle = -rodAngle;

    if (animate) {
      this.scene.tweens.add({
        targets: this.pivot,
        angle: rodAngle,
        duration: this.tiltDuration,
        ease: this.tiltEase,
      });
      this.scene.tweens.add({
        targets: [this.leftTray, this.rightTray],
        angle: trayAngle,
        duration: this.tiltDuration,
        ease: this.tiltEase,
      });
    } else {
      this.pivot.angle = rodAngle;
      this.leftTray.angle = trayAngle;
      this.rightTray.angle = trayAngle;
    }
    return this;
  }

  /** Convenience: heavier side ratio computed from numeric weights. */
  setWeights(leftKg, rightKg, animate = true) {
    const total = leftKg + rightKg;
    if (total === 0) return this.setBalance(0, animate);
    // ratio = (right - left) / total → positive when right heavier
    return this.setBalance((rightKg - leftKg) / total, animate);
  }

  /**
   * Attach `obj` to the left tray. Object position becomes relative to
   * the tray; (0, 0) sits at the rod-end-circle, so objects placed at
   * y = -dishOffset land flush on the pan surface.
   */
  placeOnLeft(obj) {
    this.leftTray.add(obj);
    this.leftTray.bringToTop(obj);
    return this;
  }

  placeOnRight(obj) {
    this.rightTray.add(obj);
    this.rightTray.bringToTop(obj);
    return this;
  }

  /** y-offset (tray-local) for the left pan surface. */
  get leftPanSurfaceY() {
    return this._leftPanSurfaceLocalY;
  }

  /** y-offset (tray-local) for the right pan surface. */
  get rightPanSurfaceY() {
    return this._rightPanSurfaceLocalY;
  }

  getLeftTrayPos() {
    return this._trayWorldPos(this.leftTray, this.leftPanSurfaceY);
  }

  getRightTrayPos() {
    return this._trayWorldPos(this.rightTray, this.rightPanSurfaceY);
  }

  _trayWorldPos(tray, localY) {
    const m = tray.getWorldTransformMatrix();
    return m.transformPoint(0, localY);
  }
}
